import type { Variant } from '../../gameTypes.js';
import {
  JumpPattern,
  type JumpPatternContext,
  type PatternPlacement,
} from './jumpPatternBase.js';

/**
 * P11 — The Corridor. A low spike capped by a short ceiling: the full jump
 * cracks the roof, so only a short hop threads over the spike and under the
 * overhang. The skill is restraint — deliberately NOT full-jumping. The overhang
 * directly above the spike is the tell. Available on every tier (a staple
 * readability test) but uncommon, so it stays a surprise.
 */
export class CorridorPattern extends JumpPattern {
  readonly id = 'corridor';
  readonly minTier: Variant = 'normal';
  readonly minAnchors = 1;
  readonly baseWeight = 0.28;

  paint(ctx: JumpPatternContext): PatternPlacement {
    // Always paints the spike; adds the ceiling when the airspace above is clear.
    ctx.stamper.corridorOverLowSpike(ctx.anchors[ctx.index].beat);
    return { consumed: 1 };
  }
}

/**
 * P13 — Saw Leap. A floor-standing blade cleared with a centred full jump (the
 * saw is two-sided, so any clip kills — the apex must sit over it). Reads "big
 * jump," same silhouette height as a tall spike. A harder-tier flourish, denser
 * inside a kiai. Falls back to a tall spike when the strip is occupied so the
 * onset still carries a full-jump obstacle.
 */
export class SawLeapPattern extends JumpPattern {
  readonly id = 'saw-leap';
  readonly minTier: Variant = 'hard';
  readonly minAnchors = 1;
  readonly baseWeight = 0.3;

  weight(ctx: JumpPatternContext): number {
    return this.baseWeight * (ctx.inKiai ? 1.6 : 1) * (ctx.tier === 'extreme' ? 1.5 : 1);
  }

  paint(ctx: JumpPatternContext): PatternPlacement {
    const beat = ctx.anchors[ctx.index].beat;
    if (ctx.stamper.saw(beat) === null) {
      ctx.stamper.tallSpike(beat);
    }
    return { consumed: 1 };
  }
}
